import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Trash2, Calendar, Phone, ChevronRight, User, Send, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const MessagesSection = ({
    contacts,
    deleteDoc,
    doc,
    db,
    setContacts,
    toast,
    formatDate,
    newNotification,
    setNewNotification,
    setNotificationRecipient,
    sendNotification,
    isMobile
}) => {
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <Card className="p-6 bg-primary/5 border-dashed border-2 border-primary/20 rounded-[2rem] group">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-300">
                            <Send size={28} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-foreground tracking-tight flex items-center gap-2">
                                Quick Broadcast
                            </h3>
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60">Send urgent push notification to every user</p>
                        </div>
                    </div>
                    <div className="flex gap-3 w-full md:w-auto flex-1 md:max-w-xl">
                        <Input
                            placeholder="Broadcast message..."
                            value={newNotification.message}
                            onChange={e => setNewNotification({ ...newNotification, message: e.target.value, title: 'Broadcast Message', type: 'info' })}
                            className="rounded-xl py-6 bg-background/50 border-border/60 font-bold tracking-tight h-[50px] flex-1"
                        />
                        <Button
                            onClick={() => { setNotificationRecipient('all'); sendNotification(); }}
                            className="rounded-xl px-8 h-[50px] bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] shadow-lg shadow-primary/20 whitespace-nowrap"
                        >
                            Send to All
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {contacts.map((c, i) => (
                    <motion.div
                        key={c.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <Card className="p-6 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2rem] hover:border-primary/20 transition-all group flex flex-col h-full">
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-black text-lg border border-primary/20 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                                        {(c.name || c.Name || 'A')[0].toUpperCase()}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-foreground text-sm tracking-tight">{c.name || c.Name || 'Anonymous User'}</h4>
                                        <p className="text-muted-foreground text-[10px] font-bold opacity-70 truncate max-w-[150px]">{c.email || c.Email}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        try {
                                            await deleteDoc(doc(db, 'contactResponses', c.id));
                                            setContacts(prev => prev.filter(x => x.id !== c.id));
                                            toast.success('Deleted');
                                        } catch (err) {
                                            toast.error('Failed to delete');
                                        }
                                    }}
                                    className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>

                            <div className="flex-1 p-5 rounded-2xl bg-secondary/20 border border-border/40 relative group-hover:bg-secondary/30 transition-colors">
                                <Mail className="absolute -top-3 -left-2 text-primary group-hover:rotate-12 transition-transform" size={20} />
                                <p className="text-muted-foreground text-sm font-medium leading-relaxed italic line-clamp-4">
                                    "{c.message || c.Message}"
                                </p>
                            </div>

                            <div className="mt-6 flex flex-col gap-3">
                                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                    <div className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(c.timestamp || c.Timestamp)}</div>
                                </div>
                                {(c.phone || c.Phone) && (
                                    <div className="flex items-center gap-2 text-xs font-bold text-primary bg-primary/5 p-3 rounded-xl border border-primary/10">
                                        <Phone size={14} /> {c.phone || c.Phone}
                                    </div>
                                )}
                            </div>
                        </Card>
                    </motion.div>
                ))}

                {contacts.length === 0 && (
                    <Card className="p-20 text-center bg-card/40 backdrop-blur-xl border-dashed border-2 border-border/60 rounded-[2.5rem] col-span-full">
                        <Mail size={48} className="mx-auto mb-4 text-muted-foreground/30" />
                        <div className="text-lg font-black text-foreground tracking-tight">Inboxes are clear!</div>
                        <p className="text-muted-foreground text-sm font-bold mt-2 opacity-60">No new messages from user contact forms.</p>
                    </Card>
                )}
            </div>
        </motion.div>
    );
};

export default MessagesSection;
